/**
 * Папка-платье: фото одного платья и ручная правка того, что собрала
 * кластеризация. Имя — инлайн; «Слить с…» забирает сюда все фото другой папки;
 * над выделенными — перенос (в другую или новую папку), обложка, снятие
 * ручной привязки, поиск похожих в Pinterest по этому фото.
 *
 * Строка «Серия CRM» — привязка платья к модели CRM по серии (`CrmSeries`): из неё
 * пост на продажу берёт серию и цену проката.
 *
 * Блок «Похожие в Pinterest» — пины Lens по обложке (или по выделенному фото),
 * пока задача поиска активна, экран опрашивает сервис.
 *
 * Блок «Референсы под это платье» (`DressReferences`) — поиск по всем фото платья
 * в библиотеках сам при открытии (бесплатно): карточки моделей «библиотека + модель»,
 * переключатель по фото платья, выделение → «Генерировать N».
 *
 * Всё, что делает человек, закрепляет состав (`manual`): имя закрепляет всю
 * папку, перенос — перенесённые фото. Следующий `photos cluster` их не тронет.
 *
 * После любой правки перезапрашиваются и папка, и список папок (`photoKeys.list`
 * — префикс всех ключей экрана, сброс по нему задевает и закэшированные чужие
 * папки, куда фото уехали). Перенос мог опустошить эту папку — сервис её
 * удалил: тогда уходим в папку, куда фото переехали.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, ChevronDown, Download, Merge, Search, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { CrmImportDialog } from "@/components/photos/CrmImportDialog";
import { CrmMatch } from "@/components/photos/CrmMatch";
import { CrmSeries } from "@/components/photos/CrmSeries";
import { DeleteDressDialog } from "@/components/photos/DeleteDressDialog";
import { DressReferences } from "@/components/photos/DressReferences";
import { DressPicker } from "@/components/photos/DressPicker";
import { PhotoGrid } from "@/components/photos/PhotoGrid";
import { PhotoSelectionBar } from "@/components/photos/PhotoSelectionBar";
import { PhotoViewer } from "@/components/photos/PhotoViewer";
import { InlineText } from "@/components/project/InlineText";
import { References } from "@/components/trends/References";
import { TaskList } from "@/components/trends/TaskList";
import { btn, btnPrimary, Chip, Empty, ErrorLine, Loading, Panel } from "@/components/trends/ui";
import { BackLink } from "@/shared/ui";
import { FittingFeed } from "@/components/fitting/FittingFeed";
import { fittingPath } from "@/lib/fitting";
import { num } from "@/lib/media";
import {
  dressTitle,
  loadDress,
  loadDressCrm,
  loadDressPins,
  loadPhotos,
  mergeDresses,
  movePhotos,
  photoKeys,
  renameDress,
  resetAuto,
  setCover,
  startLens,
  type DressCard,
  type Dress,
  type MoveResult,
} from "@/lib/photos";
import { useSelection } from "@/lib/selection";
import { isTaskActive, pollWhileActive, retryQuery } from "@/lib/trends";

const NO_DRESSES: DressCard[] = [];

export function DressPage() {
  const dressId = Number(useParams().dressId);
  const valid = Number.isInteger(dressId) && dressId > 0;
  const dress = useQuery({
    queryKey: photoKeys.dress(dressId),
    queryFn: () => loadDress(dressId),
    retry: retryQuery,
    enabled: valid,
  });
  // Список папок — для пикеров «В папку» и «Слить с…».
  const list = useQuery({ queryKey: photoKeys.list, queryFn: loadPhotos, retry: retryQuery });

  if (!valid)
    return (
      <div className="grid gap-3 p-5">
        <BackLink to="/photos">Наши фото</BackLink>
        <ErrorLine error={new Error("Неверный адрес папки.")} />
      </div>
    );
  if (dress.isPending) return <Loading />;
  if (dress.isError)
    return (
      <div className="grid gap-3 p-5">
        <BackLink to="/photos">Наши фото</BackLink>
        <ErrorLine error={dress.error} />
      </div>
    );
  // Ключ — id: в другой папке выделение и открытое фото начинаются заново.
  return <DressView key={dress.data.id} d={dress.data} dresses={list.data?.dresses ?? NO_DRESSES} />;
}

function DressView({ d, dresses }: { d: Dress; dresses: DressCard[] }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);
  const [moved, setMoved] = useState<MoveResult | null>(null);
  const { selected, selecting, setSelecting, toggle, selectVisible, clear } = useSelection(
    d.photos,
    d.photos,
    open === null,
  );
  const pinned = useMemo(() => d.photos.filter((p) => p.link_kind === "manual").length, [d.photos]);
  const card = dresses.find((x) => x.id === d.id);
  // Обложка по умолчанию — первое фото по дате: её считает список папок.
  const coverId = d.cover_photo_id ?? card?.cover?.photo_id ?? null;

  /** Удалённые сервисом папки — из кэша вон (их перезапрос — 404), остальное — перезапросить. */
  const refresh = useCallback(
    (deleted: number[] = []) => {
      for (const gone of deleted) queryClient.removeQueries({ queryKey: photoKeys.dress(gone), exact: true });
      void queryClient.invalidateQueries({ queryKey: photoKeys.dress(d.id) });
      void queryClient.invalidateQueries({ queryKey: photoKeys.list });
    },
    [queryClient, d.id],
  );

  const moveMut = useMutation({
    mutationFn: (dressId: number | null) => movePhotos([...selected], dressId),
    onSuccess: (got) => {
      clear();
      if (got.deleted_dresses.includes(d.id)) {
        // Эта папка опустела и удалена — смотреть больше нечего, идём за фото.
        navigate(`/photos/${got.dress_id}`, { replace: true });
      } else setMoved(got);
      refresh(got.deleted_dresses);
    },
  });
  const autoMut = useMutation({
    mutationFn: () => resetAuto([...selected]),
    onSuccess: () => {
      clear();
      refresh();
    },
  });
  const coverMut = useMutation({
    mutationFn: (photoId: number) => setCover(d.id, photoId),
    onSuccess: () => refresh(),
  });
  const mergeMut = useMutation({
    mutationFn: (from: number) => mergeDresses([from], d.id),
    onSuccess: (got) => {
      setMerging(false);
      refresh(got.deleted_dresses);
    },
  });

  // Тот же запрос, что у строки «Серия CRM» (общий кэш): у привязанной папки — «Доимпорт из CRM» в шапке.
  const crmLink = useQuery({ queryKey: photoKeys.crm(d.id), queryFn: () => loadDressCrm(d.id), retry: retryQuery });
  const crmModelId = crmLink.data?.crm_model_id ?? null;
  const [importOpen, setImportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleted = useCallback(() => {
    queryClient.removeQueries({ queryKey: photoKeys.dress(d.id) });
    void queryClient.invalidateQueries({ queryKey: photoKeys.list });
    navigate("/photos", { replace: true });
  }, [queryClient, navigate, d.id]);

  const lensPins = useQuery({
    queryKey: photoKeys.pins(d.id),
    queryFn: () => loadDressPins(d.id),
    retry: retryQuery,
    refetchInterval: (q) => (pollWhileActive(q.state.data?.tasks) ? 5000 : false),
  });
  const lensMut = useMutation({
    mutationFn: (photoId?: number) => startLens(d.id, photoId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: photoKeys.pins(d.id) }),
  });
  // Поиск кончился → пины новые, а в «Наших фото» ничего не менялось: перезапрос не нужен.
  const lensTasks = lensPins.data?.tasks ?? [];
  const lensActive = lensTasks.filter(isTaskActive).length;

  const rename = (name: string) =>
    renameDress(d.id, name).then((got) => {
      refresh();
      return got.name ?? "";
    });

  const close = useCallback(() => setOpen(null), []);
  const closeMerge = useCallback(() => setMerging(false), []);
  const only = selected.size === 1 ? [...selected][0] : undefined;
  const movedTitle = moved
    ? dressTitle(dresses.find((x) => x.id === moved.dress_id) ?? { id: moved.dress_id, name: null })
    : "";
  const busy = moveMut.isPending || autoMut.isPending || coverMut.isPending || lensMut.isPending;

  return (
    <div className="grid gap-4 p-5">
      <BackLink to="/photos">Наши фото</BackLink>

      <header className="grid gap-1" data-annot="шапка папки">
        <div className="flex flex-wrap items-start gap-2">
          <InlineText
            value={d.name}
            placeholder={`Платье #${d.id}`}
            onSave={rename}
            className="min-w-[240px] flex-1 text-[18px] font-semibold tracking-[-0.01em] text-fg"
            annot="имя папки"
          />
          <div className="relative">
            <button
              type="button"
              className={btn}
              aria-expanded={merging}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => setMerging((v) => !v)}
              disabled={mergeMut.isPending}
              title="Забрать в эту папку все фото другой папки; та удалится"
            >
              <Merge size={13} />
              Слить с…
              <ChevronDown size={12} />
            </button>
            {merging ? (
              <div className="absolute top-full right-0 w-[280px]">
                <DressPicker
                  dresses={dresses}
                  exclude={d.id}
                  allowNew={false}
                  onPick={(picked) => {
                    if (picked === null || mergeMut.isPending) return;
                    setMerging(false);
                    mergeMut.mutate(picked);
                  }}
                  onClose={closeMerge}
                />
              </div>
            ) : null}
          </div>
          {crmModelId !== null ? (
            <button type="button" className={btnPrimary} onClick={() => setImportOpen(true)}>
              <Download size={13} />
              Доимпорт из CRM
            </button>
          ) : null}
          <button
            type="button"
            className={btn}
            onClick={() => setDeleteOpen(true)}
            title="Убрать папку и её фото из «Наших фото»; модель из CRM можно импортировать заново"
          >
            <Trash2 size={13} />
            Удалить
          </button>
        </div>
        <DeleteDressDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          dressId={d.id}
          title={dressTitle(d)}
          onDeleted={deleted}
        />
        {crmModelId !== null ? (
          <CrmImportDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            modelId={crmModelId}
            dressId={d.id}
          />
        ) : null}
        <div className="text-[12.5px] text-fg-muted">
          <span className="tnum text-fg">{num(d.photos.length)}</span> фото
          {pinned ? (
            <>
              {" "}
              · закреплено <span className="tnum text-fg">{num(pinned)}</span>
            </>
          ) : null}
          <span className="text-fg-faint"> · имя закрепляет состав папки: кластеризация её не разнесёт</span>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12.5px]">
          <span className="text-fg-faint">Серия CRM</span>
          <CrmSeries dressId={d.id} />
          <CrmMatch dressId={d.id} photos={d.photos} />
        </div>
        <ErrorLine error={mergeMut.error} />
        {moved ? (
          <div className="w-fit rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1 text-[12px] text-ok">
            Перенесено {num(moved.moved)} в{" "}
            <Link to={`/photos/${moved.dress_id}`} className="underline underline-offset-2">
              {moved.created ? "новую папку" : movedTitle}
            </Link>
            .
          </div>
        ) : null}
      </header>

      <Panel
        title={
          <>
            Фото <span className="tnum font-normal text-fg-faint">{d.photos.length}</span>
          </>
        }
        aside={
          d.photos.length ? (
            <>
              {selected.size ? (
                <span className="text-[12px] tnum text-accent-text">выделено {num(selected.size)}</span>
              ) : null}
              <Chip
                on={selecting}
                onClick={() => setSelecting(!selecting)}
                title="Режим выделения: клик по плитке выделяет, а не открывает. Без режима — Ctrl/Cmd-клик, Shift — диапазон."
              >
                <span className="inline-flex items-center gap-1">
                  <CheckSquare size={12} />
                  выделять
                </span>
              </Chip>
            </>
          ) : null
        }
        annot="фото папки"
      >
        {d.photos.length === 0 ? (
          <Empty>В папке нет фото.</Empty>
        ) : (
          <div className={`p-3 ${selected.size ? "pb-20" : ""}`}>
            <PhotoGrid
              photos={d.photos}
              selected={selected}
              selecting={selecting}
              onToggle={toggle}
              onOpen={setOpen}
              coverId={coverId}
            />
          </div>
        )}
      </Panel>

      <Panel
        title={
          <>
            Похожие в Pinterest{" "}
            {lensPins.data?.pins.length ? (
              <span className="tnum font-normal text-fg-faint">{lensPins.data.pins.length}</span>
            ) : null}
          </>
        }
        aside={
          <button
            type="button"
            className={btnPrimary}
            onClick={() => lensMut.mutate(undefined)}
            disabled={d.photos.length === 0 || lensMut.isPending || lensActive > 0}
            title="Поиск по обложке; по другому фото — выделите его и нажмите «Искать по этому фото»"
          >
            <Search size={13} />
            Найти похожие
          </button>
        }
        annot="похожие в pinterest"
      >
        <ErrorLine error={lensMut.error} />
        {lensTasks.length ? (
          <TaskList tasks={lensTasks} limit={lensActive ? Math.max(3, lensActive) : 3} />
        ) : null}
        {lensPins.isPending ? (
          <Loading />
        ) : lensPins.isError ? (
          <ErrorLine error={lensPins.error} />
        ) : lensPins.data.pins.length === 0 ? (
          <Empty>
            {lensActive > 0
              ? "Ищем похожие в Pinterest — обычно 2–4 минуты."
              : lensTasks.length > 0
                ? "Похожих не нашлось. Попробуйте другое фото — выделите его и нажмите «Искать по этому фото»."
                : "Ещё не искали. Нажмите «Найти похожие» — поиск идёт по обложке, обычно 2–4 минуты."}
          </Empty>
        ) : (
          <References
            pins={lensPins.data.pins}
            scope="dress"
            queryKey={photoKeys.pins(d.id)}
            defaultLibraryName={() => dressTitle(d)}
            emptyText="Пинов нет."
            fittingPath={(ids) => fittingPath({ pins: ids }, d.id)}
          />
        )}
      </Panel>

      <DressReferences dress={d} />

      <Panel title="Генерации" annot="генерации">
        <FittingFeed
          filter={{ dress: d.id }}
          emptyText="Платье ещё не примеряли и не генерировали — выделите пины в «Похожих» или откройте карточку референса в библиотеке"
        />
      </Panel>

      {open !== null && d.photos[open] ? (
        <PhotoViewer photos={d.photos} index={open} onIndex={setOpen} onClose={close} />
      ) : null}
      {selected.size ? (
        <PhotoSelectionBar
          count={selected.size}
          visibleCount={d.photos.length}
          onSelectVisible={selectVisible}
          onClear={clear}
          dresses={dresses}
          currentDressId={d.id}
          onMove={(dressId) => moveMut.mutateAsync(dressId).then(() => undefined)}
          onCover={only === undefined ? null : () => coverMut.mutateAsync(only).then(() => undefined)}
          onLens={only === undefined || lensActive > 0 ? null : () => lensMut.mutateAsync(only).then(() => undefined)}
          onReset={() => autoMut.mutateAsync().then(() => undefined)}
          busy={busy}
          error={moveMut.error ?? autoMut.error ?? coverMut.error ?? lensMut.error}
        />
      ) : null}
    </div>
  );
}
